/* eslint-disable max-len */
import React, { ReactNode, useEffect, useState } from 'react';
import { FormikProps } from 'formik';
import i18n from 'i18n/config';
import moment from 'moment';
import { Moment } from 'moment-timezone';
import { useQuery } from 'react-query';
import calculateDisableDrawdownDates from 'utils/fxTrades/calculateDisableDrawdownDates';
import t from 'utils/translationHelper';

import { CurrencyAccountDto } from '@alpha/currency-accounts-dtos';
import { TradeDto } from '@alpha/fx-dtos';
import { PaymentDateOptionsDto } from '@alpha/payments-dtos';
import { Alert } from '@alpha/ui-lib/ui/Alert';
import { ActionButton } from '@alpha/ui-lib/ui/button';
import { APDatePicker } from '@alpha/ui-lib/ui/DatePicker';
import { BaseDrawer } from '@alpha/ui-lib/ui/Drawer/APBaseDrawer';
import { MenuItem } from '@alpha/ui-lib/ui/external';
import { Select } from '@alpha/ui-lib/ui/Select';
import { Typography } from '@alpha/ui-lib/ui/Typography';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { SingleStat } from '../../../../../../node_modules/@alpha/ui-lib/ui/StatSummary/SingleStat';
import useAlphaSnackbar from '../../../../../hooks/useAlphaSnackbar';
import useLog from '../../../../../hooks/useLog';
import FXTradeService from '../../../../../services/FXTrade/fxTrade.service';
import { formatNumber } from '../../../../../utils/currency.helpers';
import InputAndLabel from '../../../../Inputs/Beneficiary/InputAndLabel';
import { DrawdownRequestForm } from '../formData';

import FundingMethod from './FundingMethod';
import useStyles from './index.styles';

type Props = {
  trade?: TradeDto,
  drawdownForm: FormikProps<DrawdownRequestForm>,
  defaultSellCurrencyAccount: CurrencyAccountDto,
  padAllowed?: boolean,
  handleAmountChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleDropdownChange: (e: React.ChangeEvent<{ name?: string | undefined; value: unknown; }>, child: ReactNode) => void
}

// eslint-disable-next-line max-lines-per-function
const Drawdown: React.FC<Props> = ({
  trade,
  drawdownForm,
  defaultSellCurrencyAccount,
  padAllowed,
  handleAmountChange,
  handleDropdownChange,
}) => {
  const classes = useStyles();
  const sb = useAlphaSnackbar();
  const { logError } = useLog();
  const [insufficientFunds, setInsufficientFunds] = useState<boolean>(false);
  const [corpayMigrationStartDate, setCorpayMigrationStartDate] = useState<string | undefined>();
  const [corpayMigrationEndDate, setCorpayMigrationEndDate] = useState<string | undefined>();

  const availableDateQuery = useQuery('getPaymentDates', () => FXTradeService.getAvailableDrawdownDates(
    trade?.id || '',
  ), {
    enabled: false,
    onError: () => {
      sb.trigger(t('could_not_load_drawdown_available_date'), 'error');
      logError({ action: 'Error loading payment available date' });
    },
    onSuccess: async (data: PaymentDateOptionsDto) => {
    },
  });

  const generateMenuOptions = () => {
    if (trade) {
      return [
        <MenuItem value={trade.buyCurrencyCode}>{trade.buyCurrencyCode}</MenuItem>,
        <MenuItem value={trade.soldCurrencyCode}>{trade.soldCurrencyCode}</MenuItem>,
      ];
    }
    return <></>;
  };

  useEffect(() => {
    if (trade) {
      drawdownForm.setFieldValue('buyCurrencyCode', trade.buyCurrencyCode);
      drawdownForm.setFieldValue('sellCurrencyCode', trade.soldCurrencyCode);
    }
  }, []);

  const handleDateChange = (_date: Moment) => {
    if (_date) {
      drawdownForm.setFieldValue('valueDate', _date.format('YYYY-MM-DD'));
    }
  };

  const showFunding = (): boolean => {
    if (drawdownForm.values.fixCurrencyCode && drawdownForm.values.valueDate
      && drawdownForm.values.amount && !drawdownForm.errors.amount) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (trade) {
      availableDateQuery.refetch();
    }
  }, [trade]);

  useEffect(() => {
    checkInsufficientAmount();
  }, [drawdownForm.values.fixCurrencyCode, drawdownForm.values.amount]);

  const checkInsufficientAmount = () => {
    const amountValue = parseFloat(drawdownForm.values.amount.toString().replace(/,/g, ''));

    if (drawdownForm.values.fixCurrencyCode === drawdownForm.values.sellCurrencyCode) {
      if (defaultSellCurrencyAccount) {
        setInsufficientFunds(defaultSellCurrencyAccount.clearedBalance < amountValue);
      }
    } else if (defaultSellCurrencyAccount) {
      setInsufficientFunds(
        defaultSellCurrencyAccount.clearedBalance < (amountValue / trade?.rate!),
      );
    }
  };

  useEffect(() => {
    try {
      const accInfo = localStorage.getItem('account-info');
      if (accInfo) {
        const endDate = JSON.parse(accInfo).corpayMigrationEndDate;
        const startDate = JSON.parse(accInfo).corpayMigrationStartDate;
        setCorpayMigrationStartDate(startDate);
        setCorpayMigrationEndDate(endDate);
      }
    } catch(error) {
      logError({ action: 'Error loading account info', error });
    }
  }, []);

  const isInMigrationWindow = (valueDate: string | null) => {
    const migrationStartDate = moment(corpayMigrationStartDate).format('YYYY-MM-DD');
    const migrationEndDate = moment(corpayMigrationEndDate).format('YYYY-MM-DD');

    return moment(valueDate).isBetween(migrationStartDate, migrationEndDate, 'day', '[]');
  };

  return (
    <>
      <div>
        <Typography className={classes.tradeDetails}>{t('trade_details')}</Typography>
        <div className={classes.statsContainer}>
          <SingleStat testId="trade-number-stat" statHeader={t('trade_number')} value={trade?.contractNumber || '-'} style={{ color: '#1E8777', fontWeight: '600' }} />
          <SingleStat testId="currency-pair-stat" statHeader={t('currency_pair')} value={`${trade?.soldCurrencyCode}/${trade?.buyCurrencyCode}` || '-'} />
          {trade?.sellBalance
            && (
              <SingleStat testId="sell-balance-stat" statHeader={t('sell_balance')} value={`${formatNumber(trade?.sellBalance, 2)} ${trade?.soldCurrencyCode}` || '-'} />
            )}
          {trade?.buyBalance
            && (<SingleStat testId="buy-balance-stat" statHeader={t('buy_balance')} value={`${formatNumber(trade?.buyBalance, 2)} ${trade?.buyCurrencyCode}` || '-'} />
            )}
        </div>
        <Typography className={classes.drawdownHeading}>{t('drawdown_details')}</Typography>
        <form className={classes.formContainer}>
          <div className={classes.ccyContainer}>
            <div>
              <Typography variant="subtitle1" className="label" style={{ marginBottom: '5px' }}>
                {t('currency')}
              </Typography>
              <Select
                data-testid="fixed-currency-select"
                onChange={handleDropdownChange}
                value={drawdownForm.values.fixCurrencyCode}
                disableUnderline
                className={classes.dropdown}
              >
                {[
                  generateMenuOptions(),
                ]}
              </Select>
            </div>
            <div style={{ flexGrow: 1 }}>
              <InputAndLabel
                label={t('amount')}
                name="amount"
                id="amount"
                type="amount"
                className="amount"
                testId="drawdown-input-amount"
                onChange={handleAmountChange}
                value={!drawdownForm.touched.amount && drawdownForm.values.amount === 0 ? '' : drawdownForm.values.amount}
                error={
                  drawdownForm.touched.amount
                  && Boolean(drawdownForm.errors.amount)
                }
                helperText={
                  drawdownForm.touched.amount
                  && drawdownForm.errors.amount
                }
              />
            </div>
          </div>
          <div style={{ marginTop: '10px' }}>
            <APDatePicker
              fullWidth
              label={t('value_date')}
              language={i18n.language}
              className="datePicker"
              onChange={handleDateChange}
              value={drawdownForm.values.valueDate}
              disabled={!availableDateQuery.data}
              placeholder={t('value_date')}
              emptyLabel={t('select_date')}
              shouldDisableDate={(date) => calculateDisableDrawdownDates(date, trade?.valueDate ?? '', availableDateQuery.data, corpayMigrationEndDate)}
              autoOk
            />
          </div>
          {
            moment(trade?.valueDate).isSameOrAfter(moment.tz('Europe/London').format('YYYY-MM-DD')) && (
              <Alert
                className={classes.alert}
                variant={isInMigrationWindow(drawdownForm.values.valueDate) ? 'warning' : 'info'}
                text={isInMigrationWindow(drawdownForm.values.valueDate) ? t('value_date_adjustment_warning') : t('value_date_adjustment')}
              />
            )
          }
          {
            moment(trade?.valueDate).isBefore(moment.tz('Europe/London').format('YYYY-MM-DD')) && (
              <Alert
                className={classes.alert}
                variant="warning"
                text={t('value_date_in_past')}
              />
            )
          }
        </form>
      </div>
      {showFunding() && (
        <FundingMethod
          drawdownForm={drawdownForm}
          padAllowed={padAllowed}
          defaultAccountName={defaultSellCurrencyAccount?.friendlyName || defaultSellCurrencyAccount?.accountName || '-'}
          defaultAccountBalance={defaultSellCurrencyAccount?.clearedBalance}
          defaultAccountCurrencyCode={defaultSellCurrencyAccount?.currencyCode}
          insufficientFunds={insufficientFunds}
        />
      )}
      <div style={{
        display: 'flex', flexDirection: 'row', padding: '92px 0 22px 0', justifyContent: 'space-between', gap: '8px',
      }}
      >
        <div style={{
          position: 'absolute', transform: 'translateY(-42px)', width: '100%', left: 0,
        }}
        >
          <BaseDrawer.LineBreak />
        </div>
        <ActionButton
          style={{
            background: '#F7F7F7', color: '#212529', width: '134px', whiteSpace: 'nowrap',
          }}
          disabled
        >
          {' '}
          {t('prev')}
        </ActionButton>
        <ActionButton
          fullWidth
          disabled={drawdownForm.touched && !drawdownForm.isValid}
          onClick={() => drawdownForm.handleSubmit()}
        >
          {t('next_IT_')}
          {' '}
          <FontAwesomeIcon icon={faAngleRight as IconProp} />
        </ActionButton>
      </div>
    </>
  );
};

export default Drawdown;
